import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/concrete-driveway-lincoln-park-nj.webp";
import drivewayConstructionImage from "@/assets/driveway-construction-totowa-nj.webp";
import drivewayInstallationImage from "@/assets/concrete-driveway-installation-totowa-nj.webp";
import concreteContractorImage from "@/assets/concrete-contractor-totowa-nj.webp";
import concreteDrivewayImage from "@/assets/concrete-driveway-totowa-nj.webp";
import curbAppealImage from "@/assets/concrete-driveway-installation-kinnelon-nj.webp";
import crackedDrivewayImage from "@/assets/concrete-driveway-bloomfield-nj.webp";

const faqs = [
  {
    question: "How long does concrete driveway installation take in North Jersey?",
    answer: "Most residential driveways take 3 to 7 days to complete. That includes excavation, base prep, forming, pouring, and cure time. Weather can also play a role, especially if we're working around rain in Bergen or Essex counties."
  },
  {
    question: "Do I need a permit for a concrete driveway in North Jersey?",
    answer: "It depends on your municipality. Some towns require a permit for driveway work, and others may not. We recommend checking with your local building department before the project begins. We can help point you in the right direction."
  },
  {
    question: "Can you pour a concrete driveway over an existing surface?",
    answer: "In some cases, yes. The existing surface must be stable and in reasonable condition. We evaluate the current driveway and recommend the best approach. In many situations, removing the old surface and starting fresh produces a better long-term result."
  },
  {
    question: "Is concrete or asphalt better for a driveway?",
    answer: "Concrete lasts longer and requires less maintenance over time. Asphalt may cost less upfront but needs resealing every few years and has a shorter lifespan. Concrete also handles heavier vehicle loads without rutting or softening in summer heat."
  },
  {
    question: "How thick should a concrete driveway be?",
    answer: "That depends on the soil conditions and expected vehicle traffic. A qualified contractor will evaluate your property and recommend the appropriate thickness. Going too thin to save on materials often leads to early failure and a costly replacement."
  },
  {
    question: "Do I need rebar in my concrete driveway?",
    answer: "Rebar or wire mesh reinforces the slab and helps prevent cracking under vehicle weight. Whether your driveway requires rebar depends on the size, soil conditions, and expected use. Our crews evaluate each project and include reinforcement where it's needed."
  },
  {
    question: "How long before I can drive on new concrete?",
    answer: "Most driveways can handle vehicle traffic after 7 days. Full cure takes approximately 28 days. We recommend avoiding heavy trucks or equipment during that period so the concrete can reach its full strength."
  },
  {
    question: "What is the 90 minute rule for concrete?",
    answer: "Once concrete is mixed, it must be poured and finished within 90 minutes. After that window, the mix begins to set and becomes difficult to work with. Our crews plan every pour so the timing stays on track from delivery to finish."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

export default function ConcreteDriveways() {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isExpanded) return;
    const close = () => setIsExpanded(false);
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', close);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', close);
      window.removeEventListener('keydown', handleKey);
    };
  }, [isExpanded]);

  return (
    <>
      <Helmet>
        <title>Concrete Driveway Installation Totowa, NJ | Masonry Pro</title>
        <meta property="og:description" content="Need a new concrete driveway in North Jersey? Our masonry crews handle full installation with proper base work and ACI-standard practices on every pour." />
        <meta property="og:title" content="Concrete Driveway Installation Totowa, NJ | Masonry Pro" />
        <meta 
          name="description" 
          content="Need a new concrete driveway in North Jersey? Our masonry crews handle full installation with proper base work and ACI-standard practices on every pour." 
        />
        {/* FAQPage schema managed by SSG build script — do not duplicate here */}
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Concrete Driveway Installation",
            description: "Professional concrete driveway installation in North Jersey with proper base work and ACI-standard practices.",
            url: "/concrete-services/driveways",
            image: "https://masonry-pro.com/og/concrete-driveways.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/concrete-driveways.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/concrete-driveways.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Concrete driveway installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <img
              src={heroImage}
              alt="Concrete driveway installation in Lincoln Park, Morris County, North Jersey by Masonry Pro Construction"
              width={1200}
              height={800}
              className="sr-only"
              aria-hidden="true"
            />
            <div className="container mx-auto px-4">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Concrete Driveway Installation in Totowa, NJ Serving North Jersey:{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Built for Daily Traffic</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.8)', fontSize: '1.25rem', marginBottom: '0.5rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  A concrete driveway must be poured correctly to hold up under daily vehicle traffic. We install and replace driveways for homeowners across Passaic, Bergen, Essex, and Morris counties.
                </p>
                <p style={{ color: 'rgba(225,231,239,0.8)', fontSize: '1.25rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Our crews follow{" "}
                  <a href="https://www.concrete.org" target="_blank" rel="noopener noreferrer" style={{ color: '#d4a853' }}>ACI standards</a><sup>1</sup> on every pour. Whether you're replacing a cracked surface or starting fresh, we build driveways designed to perform for decades.
                </p>
                <p style={{ color: 'rgba(225,231,239,0.8)', fontSize: '1.1rem', marginTop: '1rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Some homeowners prefer the look and flexibility of interlocking pavers over poured concrete. If you're comparing options, we'd be happy to walk you through both during a free on-site estimate.
                </p>
              </div>
            </div>
          </section>

          {/* Proper Base Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Proper Base Prevents <span className="text-gradient-gold">Cracking and Settling</span> Under Vehicle Weight
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      We place compacted gravel beneath every driveway we pour. The gravel creates a stable foundation and gives water somewhere to go. Without it, moisture sits directly under the slab.
                    </p>
                    <p>
                      Homeowners sometimes ask if concrete can be poured directly on dirt for a driveway. We don't do it that way. Dirt shifts when it gets wet, and it moves even more when temperatures change. In North Jersey, that kind of movement can crack a new driveway within a year or two. And when vehicles are driving over it daily, the stress on the slab accelerates the damage.
                    </p>
                    <p>
                      Clay soil shows up all over Essex and Bergen counties. That type of soil holds a lot of moisture, which makes the problem worse. We lay gravel down to create separation between the clay and the concrete. ACI has standards for base preparation depending on soil type and the load the driveway will carry.<sup>1</sup> Those are the standards we follow.
                    </p>
                  </div>
                </div>
                <div
                  className="aspect-[4/3] rounded-xl overflow-hidden bg-muted cursor-zoom-in"
                  onClick={() => setIsExpanded(true)}
                >
                  <img
                    src={drivewayConstructionImage}
                    alt="Driveway construction cross-section showing surface layer, aggregate stone base layer, and subgrade in Totowa, Passaic County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>

                {isExpanded && (
                  <div
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
                    onClick={() => setIsExpanded(false)}
                  >
                    <img
                      src={drivewayConstructionImage}
                      alt="Driveway construction cross-section showing surface layer, aggregate stone base layer, and subgrade in Totowa, Passaic County, NJ"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Ground Preparation Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 lg:order-1 aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                  <img
                    src={drivewayInstallationImage}
                    alt="Rebar grid laid over gravel subbase for concrete driveway installation in Totowa, Passaic County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Ground Preparation for Driveways <span className="text-gradient-gold">Requires Extra Attention</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      We begin by excavating the area to a depth appropriate for the soil conditions and drainage requirements. The ground is graded to direct water away from your home and off the driveway. Standing water on a driveway creates problems, especially in winter when it freezes.
                    </p>
                    <p>
                      We set forms along the edges to hold the concrete in place while it cures. The forms must be level and secure. If they shift during the pour, the finished driveway won't be even.
                    </p>
                    <p>
                      Rebar or wire mesh gets placed inside the forms before the pour. A driveway carries significantly more weight than a sidewalk or walkway, so reinforcement is part of every driveway project we do.
                    </p>
                    <p>
                      Every property is a little different. Slope, soil type, and how many vehicles use the driveway all factor in. We evaluate the site before starting and adjust our prep work to match the conditions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Year-Round Installation Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    We Pour Concrete Driveways <span className="text-gradient-gold">Year-Round</span> in North Jersey
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      We pour driveways throughout the year. Each season has its own advantages, and we adjust our methods based on the conditions. The ground must be workable, and the temperature must be right for the concrete to cure properly.
                    </p>
                    <p>
                      Some homeowners in West Orange and Hackensack prefer to schedule their driveway project before summer. Others book in fall or winter when contractor schedules tend to open up. Either approach works.
                    </p>
                    <p>
                      Concrete cures best when temperatures remain between 50 and 80 degrees. When we're working outside that range, we take additional steps. In colder weather, we can use insulated blankets to protect the fresh pour. The project doesn't stop. We simply adjust our methods.
                    </p>
                    <p>
                      Once concrete is mixed, it must be poured and finished within{" "}
                      <a 
                        href="https://www.concrete.org" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-secondary hover:text-secondary/80 underline"
                      >
                        90 minutes
                      </a>
                      .<sup>6</sup> Our crews plan each pour so the timing stays on track from delivery to finish. If you're in Paramus, Clifton, or Wayne and considering a new driveway, give us a call or text.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                  <img
                    src={concreteContractorImage}
                    alt="Concrete contractor pouring wet concrete from a chute for a driveway installation in Totowa, Passaic County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Longevity Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 lg:order-1 aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                  <img
                    src={concreteDrivewayImage}
                    alt="Finished concrete driveway with clean control joints next to a residential home in Totowa, Passaic County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Well-Poured Concrete Driveway <span className="text-gradient-gold">Lasts Decades</span> in Our Climate
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      A concrete driveway is a long-term investment. When poured correctly, it can last 25 to 40 years. That's the kind of return homeowners in Livingston and Short Hills are looking for.
                    </p>
                    <p>
                      The strength of the concrete itself matters. We use ready-mix concrete that meets{" "}
                      <a 
                        href="https://www.astm.org" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-secondary hover:text-secondary/80 underline"
                      >
                        ASTM C94 specifications
                      </a>
                      .<sup>2</sup> The mix is designed for the right balance of strength, workability, and durability. Rebar or wire mesh reinforces the slab from within.
                    </p>
                    <p>
                      Control joints are cut into the surface at regular intervals. These are planned lines that give the concrete a place to expand and contract with temperature changes. Without control joints, cracks form randomly and spread over time.
                    </p>
                    <p>
                      Our winters here in North Jersey can take a toll on outdoor surfaces. Freeze-thaw cycles are relentless. Water seeps into small cracks, freezes, and then expands. That cycle breaks down poorly poured driveways over time. A proper base, quality mix, and correct finishing techniques can prevent that from happening. ACI-standard practices are what separate a driveway that lasts from one that doesn't.<sup>1</sup>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Curb Appeal Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A New Concrete Driveway Adds <span className="text-gradient-gold">Curb Appeal and Value</span> to Your Home
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      A concrete driveway does more than handle daily traffic. It can add value to your property. The{" "}
                      <a 
                        href="https://www.nar.realtor" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-secondary hover:text-secondary/80 underline"
                      >
                        National Association of Realtors
                      </a>
                      <sup>5</sup> puts exterior improvements high on the list of features that buyers notice.
                    </p>
                    <p>
                      Your driveway is one of the first things people see when they visit or drive by. A cracked, uneven surface sends one message, while a clean, well-poured driveway sends another entirely. In neighborhoods like Glen Ridge and Maplewood, that difference can matter.
                    </p>
                    <p>
                      Buyers shopping in Bergen and Essex counties often have exterior condition on their checklist. A quality driveway signals that the property has been cared for, and that can make a real difference when it comes time to sell.
                    </p>
                    <p>
                      Your family benefits from it every day, too. Pulling into a smooth, level driveway that holds up year after year is something you notice. That's value you can see, and value that holds over time.
                    </p>
                    <p>
                      Replacing a driveway is one of the larger projects most homeowners take on, and many of our customers prefer to spread the cost over monthly payments rather than pay all at once. We offer financing through our partner Acorn Finance with pre-qualified offers from a network of lenders. The pre-qualification step uses a soft credit pull, so it will not affect your credit score. <Link to="/financing" className="text-secondary hover:underline">Learn more about financing options</Link>.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                  <img
                    src={curbAppealImage}
                    alt="Finished concrete driveway with smooth surface leading to a residential garage in Kinnelon, Morris County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Common Mistakes Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 lg:order-1 aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                  <img
                    src={crackedDrivewayImage}
                    alt="Cracked and deteriorating concrete driveway showing common installation mistakes in Bloomfield, Essex County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Common Driveway Installation <span className="text-gradient-gold">Mistakes We See</span> Across North Jersey
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      Contractors who rush through a driveway pour tend to cut corners. We get calls from homeowners in Wayne, Paramus, and other towns asking us to fix concrete that wasn't done right the first time.
                    </p>
                    <p>
                      No gravel base underneath is the biggest one. We've repaired driveways that were only a couple of seasons old because they were poured directly on dirt. The slab settled unevenly, and cracks spread from there.
                    </p>
                    <p>
                      Some contractors pour the slab too thin to save on materials. That doesn't hold up, especially under vehicle weight. A driveway must be thick enough to handle cars and trucks driving over it daily. Going too thin ends up costing more when the slab fails and needs to be torn out.
                    </p>
                    <p>
                      Skipping reinforcement is another mistake we see often. A driveway without rebar or wire mesh is more likely to crack under the stress of daily use. The reinforcement holds the slab together even when hairline cracks develop.
                    </p>
                    <p>
                      Control joints are something else we see done wrong. Either they're skipped entirely or spaced too far apart. Without proper joint placement, the concrete cracks wherever it wants instead of along the planned lines.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="glass-card rounded-xl p-8">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                    How long does a <span className="text-gradient-gold">concrete driveway</span> last?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    A properly poured concrete driveway can last 25 to 40 years. In Northern New Jersey, our freeze-thaw cycles can take a toll on outdoor surfaces, so how the driveway is constructed makes a real difference.
                  </p>
                  <p className="text-foreground font-medium mb-4">Factors that affect how long your driveway will last:</p>
                  <ul className="space-y-2 text-muted-foreground mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-secondary mt-1">•</span>
                      A compacted gravel base appropriate for your soil conditions and vehicle load, per ACI standards<sup>1</sup>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-secondary mt-1">•</span>
                      Reinforcement with rebar or wire mesh inside the slab
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-secondary mt-1">•</span>
                      Properly spaced control joints to manage expansion and contraction
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-secondary mt-1">•</span>
                      Ready-mix concrete that meets ASTM C94 specifications<sup>2</sup>
                    </li>
                  </ul>
                  <p className="text-muted-foreground">
                    When a qualified masonry contractor handles the base prep, reinforcement, and finishing, your driveway can hold up for decades in our regional climate.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Category hub backlink */}
          <section className="py-8 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-muted-foreground">
                  A concrete driveway is just one part of the <Link to="/concrete-services" className="text-secondary hover:underline">concrete work we do across North Jersey</Link>, so if your project covers more than that, we can help there too.
                </p>
                <p className="text-muted-foreground mt-4">
                  If yours isn't ready for a full replacement yet, we can also <Link to="/concrete-services/repair" className="text-secondary hover:underline">repair a cracked or sinking driveway</Link> instead.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          {/* CTA Section */}
          <CTASection 
            serviceName="Concrete Driveway Installation"
            heading="Ready for a"
            highlightedHeading="New Concrete Driveway?"
            showFinancing
          />

          {/* Citations Section */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
                        <li>
                          American Concrete Institute (ACI), ACI 318: Building Code Requirements for Structural Concrete – Standards for base preparation and structural concrete requirements.{" "}
                          <a href="https://www.concrete.org" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            https://www.concrete.org
                          </a>
                        </li>
                        <li>
                          ASTM International, ASTM C94: Standard Specification for Ready-Mixed Concrete – Requirements for ready-mix concrete batching, mixing, and delivery.{" "}
                          <a href="https://www.astm.org" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            https://www.astm.org
                          </a>
                        </li>
                        <li>
                          ASTM International, ASTM C150: Standard Specification for Portland Cement – Cement standards for concrete mix design.{" "}
                          <a href="https://www.astm.org" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            https://www.astm.org
                          </a>
                        </li>
                        <li>
                          New Jersey Uniform Construction Code, Residential Code Table R301.2 – Frost line depth for Northern New Jersey.{" "}
                          <a href="https://www.nj.gov/dca/divisions/codes" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            https://www.nj.gov/dca/divisions/codes
                          </a>
                        </li>
                        <li>
                          National Association of Realtors, 2023 Remodeling Impact Report – Exterior improvements and buyer interest.{" "}
                          <a href="https://www.nar.realtor" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            https://www.nar.realtor
                          </a>
                        </li>
                        <li>
                          American Concrete Institute (ACI), ACI 332: Residential Code Requirements for Structural Concrete – Residential concrete standards including the 90 minute rule for ready-mix delivery and placement.{" "}
                          <a href="https://www.concrete.org" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                            https://www.concrete.org
                          </a>
                        </li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
